import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, InputGroup, Spinner, Alert } from "react-bootstrap";
import Modal from "../../../../Utilities/DraggableModal";
import { AgGridReact } from "ag-grid-react";
import { useMsal } from "@azure/msal-react";
import FuncFetch from "../../../../Utilities/funcFetch";

export const DEBTOR_SMS_HISTORY_REFRESH_EVENT = "debtor-sms-history:refresh";

export function notifyDebtorSmsHistoryRefresh(debtorID) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(DEBTOR_SMS_HISTORY_REFRESH_EVENT, {
      detail: { debtorID },
    })
  );
}

function querySmsHistory({ debtorID, instance, accounts }) {
  return FuncFetch(
    `${process.env.REACT_APP_API_URL}/communications/GetSMSHistory`,
    { debtorID },
    "GET",
    null,
    instance,
    accounts
  ).then((historyResponse) =>
    Array.isArray(historyResponse)
      ? historyResponse
      : Array.isArray(historyResponse?.debtorSMSHistory)
      ? historyResponse.debtorSMSHistory
      : []
  );
}

function DebtorSMSHistoryModal({ show, onHide, smsHistory, debtorID }) {
  const [rowData, setRowData] = useState(Array.isArray(smsHistory) ? smsHistory : []);
  const isMessageColumn = (key) => /(message|sms|text|body|content)/i.test(key);
  const { instance, accounts } = useMsal();
  const [hasMobilePhone, setHasMobilePhone] = useState(true);
  const [checkingMobilePhone, setCheckingMobilePhone] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState("Other");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  useEffect(() => {
    setRowData(Array.isArray(smsHistory) ? smsHistory : []);
  }, [smsHistory]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setSelectedTemplateType("Other");
    setMessage("");
  }, [show]);

  useEffect(() => {
    if (!show || !debtorID) {
      return;
    }

    setCheckingMobilePhone(true);
    FuncFetch(
      `${process.env.REACT_APP_API_URL}/communications/hasmobilephone`,
      { debtorID },
      "GET",
      null,
      instance,
      accounts
    )
      .then((response) => {
        if (response && typeof response === "object" && typeof response.hasMobilePhone === "boolean") {
          setHasMobilePhone(response.hasMobilePhone);
          return;
        }

        if (typeof response === "boolean") {
          setHasMobilePhone(response);
          return;
        }

        setHasMobilePhone(true);
      })
      .catch(() => {
        setHasMobilePhone(true);
      })
      .finally(() => setCheckingMobilePhone(false));
  }, [show, debtorID, instance, accounts]);

  const refreshSmsHistory = useCallback(() => {
    if (!debtorID) {
      setRowData([]);
      return Promise.resolve([]);
    }

    return querySmsHistory({ debtorID, instance, accounts }).then((refreshedHistory) => {
      setRowData(refreshedHistory);
      return refreshedHistory;
    });
  }, [debtorID, instance, accounts]);

  useEffect(() => {
    if (!show || !debtorID) {
      return undefined;
    }

    let isActive = true;

    const runRefresh = () => {
      setRefreshing(true);

      refreshSmsHistory()
        .catch((error) => {
          console.error("Failed to refresh SMS history", error);
        })
        .finally(() => {
          if (isActive) {
            setRefreshing(false);
          }
        });
    };

    const handleSmsHistoryRefresh = (event) => {
      const eventDebtorID = event?.detail?.debtorID;

      if (eventDebtorID && eventDebtorID !== debtorID) {
        return;
      }

      runRefresh();
    };

    runRefresh();
    window.addEventListener(DEBTOR_SMS_HISTORY_REFRESH_EVENT, handleSmsHistoryRefresh);
    const intervalId = window.setInterval(runRefresh, 60000);

    return () => {
      isActive = false;
      window.removeEventListener(DEBTOR_SMS_HISTORY_REFRESH_EVENT, handleSmsHistoryRefresh);
      window.clearInterval(intervalId);
    };
  }, [show, debtorID, refreshSmsHistory]);

  const columnDefs = useMemo(() => {
    if (!rowData.length) {
      return [];
    }

    const keys = Object.keys(rowData[0]);
    const primaryMessageKey = keys.find((key) => isMessageColumn(key));
    const orderedKeys = primaryMessageKey
      ? [primaryMessageKey, ...keys.filter((key) => key !== primaryMessageKey)]
      : keys;
    const otherColumnsFlex =
      primaryMessageKey && orderedKeys.length > 1 ? 1 / (orderedKeys.length - 1) : 1;

    return orderedKeys.map((key) => ({
      headerName: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
      field: key,
      sortable: true,
      filter: true,
      hide: /^debtorid$/i.test(key),
      flex: key === primaryMessageKey ? 1 : otherColumnsFlex,
      wrapText: isMessageColumn(key),
      autoHeight: isMessageColumn(key),
      cellStyle: isMessageColumn(key)
        ? { whiteSpace: "normal", lineHeight: "1.4", wordBreak: "break-word" }
        : undefined,
      valueFormatter: (params) => {
        if (params.value == null) {
          return "";
        }

        if (
          typeof params.value === "string" &&
          /(date|time)/i.test(key) &&
          !Number.isNaN(new Date(params.value).getTime())
        ) {
          return new Date(params.value).toLocaleString();
        }

        return params.value;
      },
    }));
  }, [rowData]);

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    minWidth: 140,
  };

  const sendSms = (messageToSend) => {
    setSending(true);
    setSendStatus(null);

    return FuncFetch(
      `${process.env.REACT_APP_API_URL}/communications/sendsms`,
      { debtorID },
      "POST",
      { message: messageToSend },
      instance,
      accounts
    )
      .then(() => {
        setRefreshing(true);
        return refreshSmsHistory();
      })
      .then(() => {
        setSendStatus({ type: "success" });
      })
      .catch((err) =>
        setSendStatus({
          type: "error",
          message: err?.message || "Failed to send message. Please try again.",
        })
      )
      .finally(() => {
        setSending(false);
        setRefreshing(false);
      });
  };

  const getTemplateMessageText = (templateResponse) => {
    if (typeof templateResponse === "string") {
      return templateResponse;
    }

    if (Array.isArray(templateResponse)) {
      const firstItem = templateResponse[0];
      if (!firstItem) {
        return "";
      }

      return (
        firstItem.smsText ||
        firstItem.message ||
        firstItem.text ||
        firstItem.templateText ||
        ""
      );
    }

    if (templateResponse && typeof templateResponse === "object") {
      return (
        templateResponse.smsText ||
        templateResponse.message ||
        templateResponse.text ||
        templateResponse.templateText ||
        templateResponse?.data?.smsText ||
        templateResponse?.data?.message ||
        templateResponse?.data?.text ||
        ""
      );
    }

    return "";
  };

  const handleTemplateChange = (templateType) => {
    setSelectedTemplateType(templateType);

    if (!templateType) {
      setMessage("");
      return;
    }

    setLoadingTemplate(true);
    setSendStatus(null);

    FuncFetch(
      `${process.env.REACT_APP_API_URL}/Communications/GetSMSTemplates`,
      { debtorID, type: templateType },
      "GET",
      null,
      instance,
      accounts
    )
      .then((templateResponse) => {
        const templateMessage = getTemplateMessageText(templateResponse);
        setMessage(templateMessage);
      })
      .catch((err) =>
        setSendStatus({
          type: "error",
          message: err?.message || "Failed to load SMS template.",
        })
      )
      .finally(() => setLoadingTemplate(false));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable className="sms-history-modal">
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2 w-100">
          <Modal.Title className="mb-0">Debtor SMS History</Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={refreshing}
            onClick={() => {
              setRefreshing(true);
              setSendStatus(null);
              refreshSmsHistory()
                .catch((err) =>
                  setSendStatus({
                    type: "error",
                    message: err?.message || "Failed to refresh SMS history.",
                  })
                )
                .finally(() => setRefreshing(false));
            }}
          >
            {refreshing ? <Spinner animation="border" size="sm" /> : "Refresh"}
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {!rowData.length ? (
          <p>No SMS history available.</p>
        ) : (
          <div className="ag-theme-quartz" style={{ height: "78vh", width: "100%" }}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              getRowHeight={() => 84}
              rowSelection="single"
              enableCellTextSelection={true}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-column align-items-stretch gap-2">
        {sendStatus?.type === "success" && (
          <Alert variant="success" className="mb-0" onClose={() => setSendStatus(null)} dismissible>
            Message sent successfully.
          </Alert>
        )}
        {sendStatus?.type === "error" && (
          <Alert variant="danger" className="mb-0" onClose={() => setSendStatus(null)} dismissible>
            {sendStatus.message}
          </Alert>
        )}
        {hasMobilePhone === false && (
          <Alert variant="warning" className="mb-0">
            No mobile phone found. Can not send SMS message.
          </Alert>
        )}
        <InputGroup>
          <Form.Select
            aria-label="SMS Template Type"
            value={selectedTemplateType}
            disabled={sending || loadingTemplate || checkingMobilePhone || hasMobilePhone === false}
            onChange={(e) => handleTemplateChange(e.target.value)}
            style={{ maxWidth: "200px" }}
          >
            <option value="">Select template</option>
            <option value="First Contact">First Contact</option>
            <option value="Follow Up">Follow Up</option>
            <option value="Payment Reminder">Payment Reminder</option>
            <option value="Payment Past Due">Payment Past Due</option>
            <option value="Payment Instructions">Payment Instructions</option>
          </Form.Select>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Type a message to send via SMS..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending || loadingTemplate || checkingMobilePhone || hasMobilePhone === false}
            style={{ resize: "vertical" }}
          />
          <Button
            variant="primary"
            disabled={sending || loadingTemplate || checkingMobilePhone || hasMobilePhone === false || !message.trim()}
            onClick={() => {
              const trimmedMessage = message.trim();
              sendSms(trimmedMessage).then(() => {
                setSelectedTemplateType("Other");
                setMessage("");
              });
            }}
          >
            {sending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Send"
            )}
          </Button>
        </InputGroup>
      </Modal.Footer>
    </Modal>
  );
}

export default DebtorSMSHistoryModal;
